import React, { useState } from 'react';
import { useAppDispatch } from '../../../store/hooks';
import { ShipmentEntry } from '../../../types/shipment';
import { Supplier } from '../../../types/supplier';
import { ShipmentEntryRow } from './ShipmentEntryRow';
import { createBlankReceivingEntry, createIngredientReceivingEntry } from '../../../store/slices/ingredientReceivingSlice';

interface ShipmentTableProps {
  receivingDate: string;
  entries: ShipmentEntry[];
  suppliers: Supplier[];
  isExpanded: boolean;
  onToggle: () => void;
  targetEntryId?: string;
  targetField?: string;
  isFlagged?: boolean;
}

export const ShipmentTable: React.FC<ShipmentTableProps> = ({
  receivingDate,
  entries,
  suppliers,
  isExpanded,
  onToggle,
  targetEntryId,
  targetField,
  isFlagged
}) => {
  const dispatch = useAppDispatch();
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [newEntry, setNewEntry] = useState<ShipmentEntry | null>(null);

  // Debug: Log the receivingDate value and first entry
  console.log('[ShipmentTable] receivingDate:', receivingDate, 'type:', typeof receivingDate);
  console.log('[ShipmentTable] First entry:', entries[0]);

  // Format date for display - parse in local timezone to avoid UTC shift
  // Handle ISO timestamp format (e.g., "2026-01-13T00:00:00.000Z") by extracting date portion
  const dateOnly = receivingDate.split('T')[0]; // Get YYYY-MM-DD from ISO timestamp or use as-is
  const [year, month, day] = dateOnly.split('-').map(Number);
  console.log('[ShipmentTable] Parsed date parts:', { year, month, day });
  const date = new Date(year, month - 1, day);
  console.log('[ShipmentTable] Created Date object:', date, 'isValid:', !isNaN(date.getTime()));
  const formattedDate = date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
  console.log('[ShipmentTable] Formatted date:', formattedDate);

  const handleAddNewEntry = () => {
    const blankEntry = createBlankReceivingEntry();
    // Set the receiving date to match this group
    blankEntry.receivingDate = receivingDate;
    setNewEntry(blankEntry);
    setIsAddingNew(true);
  };

  const handleSaveNewEntry = async (entry: ShipmentEntry) => {
    try {
      await dispatch(createIngredientReceivingEntry(entry)).unwrap();
      setIsAddingNew(false);
      setNewEntry(null);
    } catch (error) {
      console.error('Failed to create entry:', error);
      alert('Failed to create entry. Please try again.');
    }
  };

  const handleCancelNewEntry = () => {
    setIsAddingNew(false);
    setNewEntry(null);
  };

  const buttonStyle: React.CSSProperties = {
    padding: '8px 16px',
    borderRadius: '4px',
    border: 'none',
    fontWeight: '500',
    cursor: 'pointer',
    fontSize: '14px',
    transition: 'background-color 0.2s'
  };

  return (
    <div
      style={{
        border: '1px solid #ddd',
        borderRadius: '8px',
        marginBottom: '20px',
        backgroundColor: 'white',
        overflow: 'hidden'
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: '16px',
          backgroundColor: '#f8f9fa',
          borderBottom: '2px solid #e0e0e0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          cursor: 'pointer'
        }}
        onClick={onToggle}
      >
        <h4 style={{ margin: 0, fontSize: '1.1rem', color: '#333' }}>
          <span style={{ marginRight: '8px' }}>{isExpanded ? '▼' : '▶'}</span>
          Received on {formattedDate} ({entries.length} {entries.length === 1 ? 'entry' : 'entries'})
        </h4>

        <div style={{ display: 'flex', gap: '8px' }} onClick={e => e.stopPropagation()}>
          <button
            onClick={handleAddNewEntry}
            style={{
              ...buttonStyle,
              backgroundColor: '#4CAF50',
              color: 'white'
            }}
            onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#45a049')}
            onMouseLeave={e => (e.currentTarget.style.backgroundColor = '#4CAF50')}
          >
            + Add Entry
          </button>
        </div>
      </div>

      {/* Table */}
      {isExpanded && (
        <div style={{ overflowX: 'auto' }}>
          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: '14px'
            }}
          >
            <thead>
              <tr>
                <th
                  style={{
                    padding: '12px 8px',
                    backgroundColor: '#f8f9fa',
                    fontWeight: '600',
                    textAlign: 'left',
                    borderBottom: '2px solid #e0e0e0',
                    textTransform: 'uppercase',
                    fontSize: '12px',
                    letterSpacing: '0.5px',
                    color: '#555'
                  }}
                >
                  Supplier Name
                </th>
                <th
                  style={{
                    padding: '12px 8px',
                    backgroundColor: '#f8f9fa',
                    fontWeight: '600',
                    textAlign: 'left',
                    borderBottom: '2px solid #e0e0e0',
                    textTransform: 'uppercase',
                    fontSize: '12px',
                    letterSpacing: '0.5px',
                    color: '#555'
                  }}
                >
                  Ingredient
                </th>
                <th
                  style={{
                    padding: '12px 8px',
                    backgroundColor: '#f8f9fa',
                    fontWeight: '600',
                    textAlign: 'left',
                    borderBottom: '2px solid #e0e0e0',
                    textTransform: 'uppercase',
                    fontSize: '12px',
                    letterSpacing: '0.5px',
                    color: '#555'
                  }}
                >
                  Storage Type
                </th>
                <th
                  style={{
                    padding: '12px 8px',
                    backgroundColor: '#f8f9fa',
                    fontWeight: '600',
                    textAlign: 'left',
                    borderBottom: '2px solid #e0e0e0',
                    textTransform: 'uppercase',
                    fontSize: '12px',
                    letterSpacing: '0.5px',
                    color: '#555'
                  }}
                >
                  Lot #
                </th>
                <th
                  style={{
                    padding: '12px 8px',
                    backgroundColor: '#f8f9fa',
                    fontWeight: '600',
                    textAlign: 'left',
                    borderBottom: '2px solid #e0e0e0',
                    textTransform: 'uppercase',
                    fontSize: '12px',
                    letterSpacing: '0.5px',
                    color: '#555'
                  }}
                >
                  Quantity
                </th>
                <th
                  style={{
                    padding: '12px 8px',
                    backgroundColor: '#f8f9fa',
                    fontWeight: '600',
                    textAlign: 'left',
                    borderBottom: '2px solid #e0e0e0',
                    textTransform: 'uppercase',
                    fontSize: '12px',
                    letterSpacing: '0.5px',
                    color: '#555'
                  }}
                >
                  Condition Upon Arrival
                </th>
                <th
                  style={{
                    padding: '12px 8px',
                    backgroundColor: '#f8f9fa',
                    fontWeight: '600',
                    textAlign: 'left',
                    borderBottom: '2px solid #e0e0e0',
                    textTransform: 'uppercase',
                    fontSize: '12px',
                    letterSpacing: '0.5px',
                    color: '#555'
                  }}
                >
                  Receiving Temp
                </th>
                <th
                  style={{
                    padding: '12px 8px',
                    backgroundColor: '#f8f9fa',
                    fontWeight: '600',
                    textAlign: 'left',
                    borderBottom: '2px solid #e0e0e0',
                    textTransform: 'uppercase',
                    fontSize: '12px',
                    letterSpacing: '0.5px',
                    color: '#555'
                  }}
                >
                  Received By
                </th>
                <th
                  style={{
                    padding: '12px 8px',
                    backgroundColor: '#f8f9fa',
                    fontWeight: '600',
                    textAlign: 'left',
                    borderBottom: '2px solid #e0e0e0',
                    textTransform: 'uppercase',
                    fontSize: '12px',
                    letterSpacing: '0.5px',
                    color: '#555'
                  }}
                >
                  Corrective Action
                </th>
                <th
                  style={{
                    padding: '12px 8px',
                    backgroundColor: '#f8f9fa',
                    fontWeight: '600',
                    textAlign: 'center',
                    borderBottom: '2px solid #e0e0e0',
                    textTransform: 'uppercase',
                    fontSize: '12px',
                    letterSpacing: '0.5px',
                    color: '#555',
                    width: '100px'
                  }}
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {/* Existing Entries */}
              {entries.map(entry => (
                <ShipmentEntryRow
                  key={entry.id}
                  entry={entry}
                  suppliers={suppliers}
                  isHighlighted={entry.id === targetEntryId}
                  targetField={entry.id === targetEntryId ? targetField : undefined}
                  isFlagged={entry.id === targetEntryId ? isFlagged : undefined}
                />
              ))}

              {/* New Entry Row */}
              {isAddingNew && newEntry && (
                <ShipmentEntryRow
                  key="new-entry"
                  entry={newEntry}
                  suppliers={suppliers}
                  isNew={true}
                  onSave={handleSaveNewEntry}
                  onCancel={handleCancelNewEntry}
                />
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
